/**
 * ir-logic.ts — pure IR logic extracted from ir.ts (jsdom-free seam).
 *
 * Epic: docs/epics/active/ts-unit-testability-seams-260623/main.md
 * Phase 2 (open editor seam front — ir.ts logic seam).
 *
 * This module contains the pure functional core of ir.ts:
 *   - All IR types and interfaces (no DOM/canvas/window deps)
 *   - `IRError` — thrown on invalid mutations
 *   - `ROLES` and `ROLES_SET` — valid PDF structure role constants
 *   - `buildIRFromStructTree(structNodes)` — flat struct list → Document IR
 *   - `findBlockById(blocks, structId)` — recursive block lookup
 *   - `mutateBlocksById(blocks, structId, fn)` — pure block-walk mutator
 *   - `mutateInlineById(blocks, structId, afterText)` — pure inline-walk mutator
 *   - `coerceRole(block, newRole)` — pure block role coercion
 *   - `serializeIRToStructList(ir)` — IR → flat struct-node list (round-trip)
 *
 * What stays in ir.ts:
 *   - `apply(ir, op)` — reducer dispatcher (imports from reducers/ which
 *     import back from ir.ts; that circular loop must stay in ir.ts to
 *     avoid a circular dependency through ir-logic.ts)
 *   - Re-exports of everything from ir-logic.ts for backward compatibility
 *
 * No DOM globals accessed at module scope — import-safe in any environment
 * including Node.js vitest without a jsdom shim.
 *
 * @module ir-logic
 */
import { log } from "./log.js";
// ─── Error class ─────────────────────────────────────────────────────────────
export class IRError extends Error {
    code;
    constructor(code, detail) {
        super(detail ? `${code}: ${detail}` : code);
        this.name = "IRError";
        this.code = code;
    }
}
// ─── Valid roles ──────────────────────────────────────────────────────────────
/**
 * Enum-like list of all valid PDF structure roles.
 * Matches what the WASM side and the edit panel dropdown use.
 */
export const ROLES = Object.freeze([
    "Document", "Art", "Sect", "Div", "BlockQuote", "Caption",
    "TOC", "TOCI", "Index", "NonStruct", "Private",
    "P", "H", "H1", "H2", "H3", "H4", "H5", "H6",
    "L", "LI", "Lbl", "LBody",
    "Table", "TR", "TH", "TD", "THead", "TBody", "TFoot",
    "Span", "Quote", "Note", "Reference", "BibEntry", "Code",
    "Link", "Annot", "Ruby", "Warichu",
    "Figure", "Formula", "Form", "Artifact",
]);
// Exported for reducers/ which consume this for role-validation in setRoleReducer.
// Phase FB.3 of frontend-mv-architecture-260524.md.
export const ROLES_SET = new Set(ROLES);
// ─── buildIRFromStructTree ────────────────────────────────────────────────────
/**
 * Parse a flat struct-tree list (as returned by interop.getStructTree().nodes)
 * into a hierarchical Document IR.
 *
 * Path-form structIds encode the tree structure: "S/0" is a top-level block,
 * "S/0/0" is its first child, "S/0/1" its second child, etc.
 * Children of a node appear immediately after the node in depth-first order.
 * The IR represents the tree by nesting blocks inside their parent blocks
 * (e.g. ListItem.content, Table.rows[].cells[]).
 *
 * Top-level blocks are those whose path parent is "S" (i.e. depth 1).
 * Deeper paths are children that belong inside composite blocks:
 *   - Table → TR → TH/TD
 *   - List  → LI (ListItem)
 *   - LI    → nested blocks
 *
 * All other nodes become top-level blocks regardless of depth, because the IR
 * treats only Table/List as structural composites. Headings, Paragraphs, etc.
 * that happen to be children in the struct tree appear as siblings at the IR
 * block level in reading order.
 */
export function buildIRFromStructTree(structNodes) {
    if (!Array.isArray(structNodes)) {
        throw new IRError("InvalidInput", "structNodes must be an array");
    }
    // Build a map from structId to raw node for O(1) parent lookup.
    const byId = new Map();
    for (const n of structNodes) {
        if (n?.structId)
            byId.set(n.structId, n);
    }
    // Collect all nodes that belong to special composites (Table, List cells)
    // so we can skip them when building the top-level block list.
    const handledAsChild = new Set();
    /**
     * Find direct children of `parentId` from the flat list.
     * A child of "S/0" has structId "S/0/<N>" (depth increases by exactly 1).
     */
    function directChildren(parentId) {
        // eslint-disable-next-line no-restricted-syntax -- portable-path-string-split: StructId is a tag-tree address ("S/0/3/2"), not a filesystem path; forward slash is the canonical separator by spec, not platform-dependent.
        const depth = parentId.split("/").length;
        const prefix = parentId + "/";
        return structNodes.filter((n) => {
            if (!n?.structId)
                return false;
            if (!n.structId.startsWith(prefix))
                return false;
            // eslint-disable-next-line no-restricted-syntax -- portable-path-string-split: same as above — StructId is a domain-defined address, not a filesystem path.
            return n.structId.split("/").length === depth + 1;
        });
    }
    /**
     * Convert a raw struct node to an IR Inline[] for text-based blocks.
     */
    function nodeToInlines(n) {
        const text = n.text ?? "";
        return [{ kind: "Text", value: text }];
    }
    /**
     * Convert a raw struct node to a TableCell.
     */
    function nodeToCell(n) {
        handledAsChild.add(n.structId);
        return {
            structId: n.structId,
            cellType: (n.role === "TH") ? "TH" : "TD",
            content: nodeToInlines(n),
        };
    }
    /**
     * Convert a raw TR node to a TableRow.
     */
    function nodeToRow(n) {
        handledAsChild.add(n.structId);
        const children = directChildren(n.structId);
        const cells = children.map(nodeToCell);
        return { structId: n.structId, cells };
    }
    /**
     * Mark a struct node AND all its descendants (by path prefix) as handled,
     * so the top-level block loop skips them. Mirrors the C# deep
     * handled-as-child marking in BuildListItem (§3.2) — pre-fix only the LI
     * and its DIRECT children were marked, leaking Span grandchildren as orphan
     * top-level cards (the 159-176 defect).
     */
    function markSubtreeHandled(structId) {
        handledAsChild.add(structId);
        const prefix = structId + "/";
        for (const node of structNodes) {
            if (node?.structId && node.structId.startsWith(prefix)) {
                handledAsChild.add(node.structId);
            }
        }
    }
    /**
     * Effective aggregated text for a struct subtree, in /K document order,
     * with /ActualText REPLACEMENT semantics (Phase-1b R1). A node carrying a
     * non-empty actualText contributes EXACTLY that override and its descendants
     * contribute nothing further — the override REPLACES the subtree's text,
     * matching how the item edit loop writes /ActualText on the LI (a naive
     * additive recursion would emit "override + stale span text" on re-fetch and
     * break the round-trip). A node without an override contributes its own
     * direct text followed by the recursively-aggregated text of each child,
     * space-joined. Mirrors the C# AggregateItemText for C#/JS parity (rule #11).
     */
    function aggregateItemText(n) {
        const override_ = n.actualText;
        if (override_ !== null && override_ !== undefined && override_ !== "") {
            return override_;
        }
        const parts = [];
        const own = n.text ?? "";
        if (own.length > 0)
            parts.push(own);
        for (const c of directChildren(n.structId)) {
            const childText = aggregateItemText(c);
            if (childText.length > 0)
                parts.push(childText);
        }
        return parts.join(" ");
    }
    /**
     * Convert a raw LI node to a ListItem (content is one Paragraph block).
     */
    function nodeToListItem(n) {
        // Deep handled-as-child marking (§3.2): mark the LI and every descendant
        // so the final block loop cannot re-project Lbl/LBody/Span grandchildren
        // (or a nested inner list — R4) as spurious top-level blocks.
        markSubtreeHandled(n.structId);
        // Recursive descendant aggregation with /ActualText replacement (R1).
        const text = aggregateItemText(n);
        const para = {
            kind: "Paragraph",
            structId: n.structId,
            content: [{ kind: "Text", value: text }],
            page: n.page ?? 1,
        };
        return { structId: n.structId, content: [para] };
    }
    /**
     * Convert a single raw struct node to a Block.
     * Returns null for node kinds that are handled as children (cells, rows, etc.)
     */
    function nodeToBlock(n) {
        const role = n.role || "";
        const page = n.page ?? 1;
        const id = n.structId;
        // Headings
        const headingMatch = role.match(/^H([1-6])$/);
        if (headingMatch) {
            return {
                kind: "Heading",
                structId: id,
                level: parseInt(headingMatch[1], 10), // WHY: regex ^H([1-6])$ guarantees capture group 1
                content: nodeToInlines(n),
                page,
            };
        }
        if (role === "H") {
            return {
                kind: "Heading",
                structId: id,
                level: 1,
                content: nodeToInlines(n),
                page,
            };
        }
        if (role === "Figure") {
            return {
                kind: "Figure",
                structId: id,
                alt: n.altText ?? "",
                decorative: !!n.isDecorative,
                imageRef: null,
                page,
                actualText: n.actualText ?? null,
            };
        }
        if (role === "Formula") {
            return {
                kind: "Formula",
                structId: id,
                latex: n.text ?? "",
                alt: n.altText ?? "",
                actualText: n.actualText ?? null,
                page,
            };
        }
        if (role === "Caption") {
            return {
                kind: "Caption",
                structId: id,
                content: nodeToInlines(n),
                page,
            };
        }
        if (role === "Note") {
            return {
                kind: "Note",
                structId: id,
                content: nodeToInlines(n),
                page,
            };
        }
        if (role === "Span") {
            return {
                kind: "Span",
                structId: id,
                content: nodeToInlines(n),
                page,
            };
        }
        if (role === "Artifact") {
            return {
                kind: "Artifact",
                structId: id,
                raw: n.text ?? "",
                page,
            };
        }
        if (role === "Table") {
            const rowNodes = directChildren(id).filter((c) => c.role === "TR");
            const rows = rowNodes.map(nodeToRow);
            // Also mark any header/footer groups as handled.
            const groups = directChildren(id).filter((c) => ["THead", "TBody", "TFoot"].includes(c.role));
            for (const g of groups) {
                handledAsChild.add(g.structId);
                const gRows = directChildren(g.structId).filter((c) => c.role === "TR");
                rows.push(...gRows.map(nodeToRow));
            }
            return {
                kind: "Table",
                structId: id,
                rows,
                page,
            };
        }
        if (role === "L") {
            const liNodes = directChildren(id).filter((c) => c.role === "LI");
            const items = liNodes.map(nodeToListItem);
            return {
                kind: "List",
                structId: id,
                ordered: false,
                items,
                page,
            };
        }
        // Everything else (P, Div, Sect, BlockQuote, Document, etc.) → Paragraph
        return {
            kind: "Paragraph",
            structId: id,
            content: nodeToInlines(n),
            page,
        };
    }
    // Build the flat block list in document order, skipping nodes that are
    // handled as children of composite blocks.
    const blocks = [];
    for (const n of structNodes) {
        if (!n?.structId)
            continue;
        if (handledAsChild.has(n.structId))
            continue;
        const block = nodeToBlock(n);
        if (block)
            blocks.push(block);
    }
    return {
        title: null,
        language: null,
        blocks,
        attestation: null,
    };
}
/**
 * Find a block by structId, recursing into composite blocks (List items).
 * Returns `null` if not found. Used by reducers to compute `dirtyPages`
 * AFTER the mutation has been applied.
 *
 * O(N-blocks) walk; the per-page index in `state.blocksByPage` is the
 * fast path for the dispatcher's downstream consumers — this helper is
 * for the reducer's one-shot lookup at the dispatch boundary.
 */
export function findBlockById(blocks, structId) {
    for (const b of blocks) {
        if (b.structId === structId)
            return b;
        if (b.kind === "List") {
            for (const item of b.items) {
                const found = findBlockById(item.content, structId);
                if (found !== null)
                    return found;
            }
        }
    }
    return null;
}
// ─── Reducer helpers ──────────────────────────────────────────────────────────
/**
 * Walk all blocks (recursively) and apply `fn` to the one matching `structId`.
 * Returns a new array if any block changed; otherwise the same reference.
 */
export function mutateBlocksById(blocks, structId, fn) {
    let changed = false;
    const next = blocks.map((b) => {
        if (b.structId === structId) {
            changed = true;
            return fn(b);
        }
        // Recurse into composite blocks.
        if (b.kind === "List") {
            const nextItems = b.items.map((item) => {
                const nextContent = mutateBlocksById(item.content, structId, fn);
                if (nextContent === item.content)
                    return item;
                changed = true;
                return { ...item, content: nextContent };
            });
            if (nextItems === b.items)
                return b;
            return { ...b, items: nextItems };
        }
        return b;
    });
    return changed ? next : blocks;
}
/**
 * Walk all blocks and mutate a Run inline (kind="Run") whose `structId`
 * matches the given `structId`, replacing its text content with `afterText`.
 *
 * Return value: `{ blocks, found }` where
 *   - `blocks` is the (possibly new) top-level block array;
 *   - `found` is `true` iff a matching Run was located and replaced.
 *
 * When `found === false`, `blocks` is the *same* reference as the input (no
 * allocation). When `found === true`, `blocks` is a new array (at least one
 * level was copied).
 */
export function mutateInlineById(blocks, structId, afterText) {
    let found = false;
    function mutateInlines(inlines) {
        const next = inlines.map((inline) => {
            if (inline.kind === "Run" && inline.structId === structId) {
                found = true;
                return { ...inline, content: [{ kind: "Text", value: afterText }] };
            }
            if (inline.kind === "Run" && inline.content) {
                const nextContent = mutateInlines(inline.content);
                if (nextContent === inline.content)
                    return inline;
                return { ...inline, content: nextContent };
            }
            return inline;
        });
        return found ? next : inlines;
    }
    function mutateBlockInlines(block) {
        if ("content" in block && Array.isArray(block.content) && block.content[0]?.kind) {
            // content is Inline[]
            const nextContent = mutateInlines(block.content);
            if (nextContent === block.content)
                return block;
            return { ...block, content: nextContent };
        }
        return block;
    }
    const nextBlocks = blocks.map((b) => {
        const next = mutateBlockInlines(b);
        if (found)
            return next;
        if (b.kind === "List") {
            const nextItems = b.items.map((item) => {
                const nextContent = item.content.map((ib) => {
                    const n = mutateBlockInlines(ib);
                    return n;
                });
                if (nextContent.every((v, i) => v === item.content[i]))
                    return item;
                return { ...item, content: nextContent };
            });
            if (nextItems.every((v, i) => v === b.items[i]))
                return b;
            return { ...b, items: nextItems };
        }
        return next;
    });
    return { blocks: found ? nextBlocks : blocks, found };
}
const _unsupportedRoleCoercions = [];
/** Snapshot of drift diagnostics recorded since the last reset (telemetry-visible). */
export function getUnsupportedRoleCoercions() {
    return _unsupportedRoleCoercions.slice();
}
/** Clear the drift-diagnostic buffer (test isolation). */
export function resetUnsupportedRoleCoercions() {
    _unsupportedRoleCoercions.length = 0;
}
/** Tokens that legitimately resolve to a Paragraph block (NOT a drift downgrade). */
const PARAGRAPH_ROLE_TOKENS = new Set(["P", "Paragraph"]);
export function buildBlockForRole(newRole, id, page, common, src) {
    const text = src.text ?? "";
    const content = (src.content && src.content.length > 0)
        ? src.content
        : [{ kind: "Text", value: text }];
    const headingMatch = newRole.match(/^H([1-6])$/);
    if (headingMatch || newRole === "H") {
        const level = (headingMatch ? parseInt(headingMatch[1], 10) : 1); // WHY: regex ^H([1-6])$ guarantees capture group 1
        return { ...common, kind: "Heading", structId: id, level, content, page };
    }
    if (newRole === "Figure") {
        return { ...common, kind: "Figure", structId: id, alt: text, decorative: false, imageRef: null, page, actualText: null };
    }
    if (newRole === "Formula") {
        return { ...common, kind: "Formula", structId: id, latex: text, alt: "", actualText: null, page };
    }
    if (newRole === "Artifact") {
        return { ...common, kind: "Artifact", structId: id, raw: text, page };
    }
    if (newRole === "Note") {
        return { ...common, kind: "Note", structId: id, content, page };
    }
    if (newRole === "Caption") {
        return { ...common, kind: "Caption", structId: id, content, page };
    }
    if (newRole === "Span") {
        return { ...common, kind: "Span", structId: id, content, page };
    }
    if (newRole === "L" || newRole === "List") {
        const item = {
            structId: `${id}-li0`,
            content: [{ kind: "Paragraph", structId: `${id}-li0-p`, content, page }],
        };
        return { ...common, kind: "List", structId: id, ordered: false, items: [item], page };
    }
    if (newRole === "Table") {
        const cell = { structId: `${id}-r0c0`, cellType: "TD", content };
        const row = { structId: `${id}-r0`, cells: [cell] };
        return { ...common, kind: "Table", structId: id, rows: [row], page };
    }
    // Default: everything else → Paragraph. "P"/"Paragraph" is the sanctioned
    // Paragraph create-role; ANY OTHER token reaching here is an exposed-but-
    // unsupported role (popover / role-set drift) — record an observable structured
    // diagnostic (rule #53) AND warn, rather than silently downgrading fidelity
    // (fork 4a; A.14). CRP-1 keeps ROLE_TREE ⊆ supported so this stays unreachable
    // in the shipped UI; a fired diagnostic means the popover parity control was
    // bypassed and a role was exposed without a backing block kind.
    if (!PARAGRAPH_ROLE_TOKENS.has(newRole)) {
        _unsupportedRoleCoercions.push({ role: newRole, structId: id });
        log.warn("[editor:ir-logic] unsupported create-role coerced to Paragraph (exposed-but-unsupported drift)", { role: newRole, structId: id });
    }
    return { ...common, kind: "Paragraph", structId: id, content, page };
}
/**
 * Coerce a block's kind and role to `newRole`, preserving as much content as
 * possible. Returns a NEW block object; input block is not mutated. Delegates
 * the role→block mapping to {@link buildBlockForRole} (the shared seam) after
 * extracting the source block's content + a text fallback and the inherited
 * BlockCommon fields.
 *
 * Used exclusively by `setRoleReducer` (reducers/text-reducers.ts) via `apply()`.
 */
export function coerceRole(block, newRole) {
    // Carry the inherited BlockCommon fields across the kind transition. A
    // role change re-shapes only the ROLE-SPECIFIC leg (kind + content/alt/…);
    // the geometry (`bbox`) and the other BlockCommon metadata must survive it.
    // Dropping `bbox` here silently strips a draw-created element's ONLY
    // geometry source and breaks GroundTruthDoc export (GT-INV-1b, fail-loud).
    const common = {};
    if (block.bbox !== undefined)
        common.bbox = block.bbox;
    if (block.issue !== undefined)
        common.issue = block.issue;
    if (block.insertedByDocableLongDescription !== undefined) {
        common.insertedByDocableLongDescription = block.insertedByDocableLongDescription;
    }
    const content = ("content" in block && Array.isArray(block.content)) ? block.content : undefined;
    const text = ("raw" in block ? block.raw
        : ("latex" in block ? block.latex
            : ("alt" in block ? block.alt : "")));
    // Build `src` conditionally: under exactOptionalPropertyTypes the optional
    // `content?` property must be OMITTED when absent, never set to `undefined`.
    const src = { text };
    if (content !== undefined)
        src.content = content;
    return buildBlockForRole(newRole, block.structId, block.page, common, src);
}
/**
 * Serialize the IR back to a flat struct-node list (same shape as
 * `interop.getStructTree().nodes`). Used for round-trip testing and for
 * building the op payload on batch saves.
 *
 * Round-trip invariant: for any `ir` produced by `buildIRFromStructTree(nodes)`,
 * `serializeIRToStructList(ir)` returns a list that encodes the same role,
 * text, page, altText, and isDecorative values as the original nodes (modulo
 * the normalisation that `buildIRFromStructTree` applies — e.g., all H1..H6
 * come back as `role="H1"`.."H6"`; bare `H` comes back as `H1`; composite
 * children such as TR/TH/TD are re-emitted as children of their parent).
 */
export function serializeIRToStructList(ir) {
    const out = [];
    function inlineText(inlines) {
        if (!inlines)
            return "";
        return inlines.map((il) => {
            if (il.kind === "Text")
                return il.value;
            if (il.kind === "Run")
                return inlineText(il.content);
            return "";
        }).join("");
    }
    function emitBlock(b) {
        switch (b.kind) {
            case "Heading":
                out.push({ structId: b.structId, role: `H${b.level}`, text: inlineText(b.content), page: b.page, altText: null, isDecorative: false });
                break;
            case "Paragraph":
                out.push({ structId: b.structId, role: "P", text: inlineText(b.content), page: b.page, altText: null, isDecorative: false });
                break;
            case "Figure":
                out.push({ structId: b.structId, role: "Figure", text: "", page: b.page, altText: b.alt, isDecorative: b.decorative });
                break;
            case "Formula":
                // altText stays null on the SERIALIZE (save) path: mapping the
                // read-side /Alt+/ActualText back onto the save wire is the
                // /Alt-vs-/ActualText storage fork (RCA §G), deliberately not
                // resolved here — this Epic phase is editor-DISPLAY only.
                out.push({ structId: b.structId, role: "Formula", text: b.latex, page: b.page, altText: null, isDecorative: false });
                break;
            case "Caption":
                out.push({ structId: b.structId, role: "Caption", text: inlineText(b.content), page: b.page, altText: null, isDecorative: false });
                break;
            case "Note":
                out.push({ structId: b.structId, role: "Note", text: inlineText(b.content), page: b.page, altText: null, isDecorative: false });
                break;
            case "Span":
                out.push({ structId: b.structId, role: "Span", text: inlineText(b.content), page: b.page, altText: null, isDecorative: false });
                break;
            case "Artifact":
                out.push({ structId: b.structId, role: "Artifact", text: b.raw, page: b.page, altText: null, isDecorative: false });
                break;
            case "Table":
                out.push({ structId: b.structId, role: "Table", text: "", page: b.page, altText: null, isDecorative: false });
                for (const row of b.rows) {
                    out.push({ structId: row.structId, role: "TR", text: "", page: b.page, altText: null, isDecorative: false });
                    for (const cell of row.cells) {
                        out.push({ structId: cell.structId, role: cell.cellType, text: inlineText(cell.content), page: b.page, altText: null, isDecorative: false });
                    }
                }
                break;
            case "List":
                out.push({ structId: b.structId, role: "L", text: "", page: b.page, altText: null, isDecorative: false });
                for (const item of b.items) {
                    out.push({ structId: item.structId, role: "LI", text: "", page: b.page, altText: null, isDecorative: false });
                    for (const ib of item.content)
                        emitBlock(ib);
                }
                break;
        }
    }
    for (const b of ir.blocks)
        emitBlock(b);
    return out;
}
